import type {
  ILibraryTopology,
  SentryOptions,
} from '@wix/editor-elements-registry/2.0/dataBinding'
import type { IDataBindingComponentModel } from '@wix/editor-elements-types/dataBinding'
import { registry as bindingDefinitionsRegistry } from '@wix/editor-elements-registry/2.0/dataBinding'
import {
  getWhitelistLibraries,
  getComponentsLibrariesFromURL,
} from '@wix/editor-elements-registry/2.0/toolbox'
import type ExperimentsManager from '@wix/wix-experiments'
import * as Sentry from '@sentry/browser'

import { bindingDefinitionsByType } from './binding-definitions/components'
import type { DataBindingComponentModel } from '@wix/data-binding-registry-sdk'
import {
  entries,
  isMobileAppEditor,
  type EditorSDK,
  type Logger,
} from '@wix/wix-data-client-common-standalone'
import type { I18nextProviderProps } from 'react-i18next'
import { eeDefinitionsByType } from './binding-definitions/components/editor-elements'
import { mobuiDefinitionsByType } from './binding-definitions/components/mob-ui-elements'
import type { Features } from '@wix/wix-data-client-common'

export interface Services {
  locale: string
  i18n: I18nextProviderProps['i18n']
  logger: Logger
  editorSdkProxy: EditorSDK
  experimentsManager: ExperimentsManager
  features: Features
}

export async function getDataBindingDefinitionsLibraries({
  editorSdkProxy,
  experimentsManager,
}: Services): Promise<ILibraryTopology[]> {
  const useNewStatics = experimentsManager.enabled(
    'specs.editor-elements.UseNewStatics',
  )
  const experimentalMobileLibrary = isMobileAppEditor()

  /**
   * get all artifact ids from the whitelist
   */
  const artifactIds = Array.from<{ artifactId: string; namespace: string }>(
    new Set(
      getWhitelistLibraries({
        useNewStatics,
        experimentalMobileLibrary,
      }),
    ),
  )

  /**
   * get URLs for GAed artifact's versions
   */
  const artifactUrls = await editorSdkProxy.document.info.getArtifactsUrls(
    artifactIds.map(({ artifactId }) => artifactId),
  )

  /**
   * get all `*-override` query params
   */
  const urlOverrides = await editorSdkProxy.document.info.getArtifactOverrides()

  const url = `?${Object.entries(urlOverrides)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')}`

  const libraries = [
    ...artifactIds.map(lib => ({
      ...lib,
      url: artifactUrls[lib.artifactId],
    })),
    ...getComponentsLibrariesFromURL(url),
  ]

  return libraries
}

const initBindingDefinitionsRegistry = async function (
  libraries: ILibraryTopology[],
) {
  return bindingDefinitionsRegistry({
    mode: 'eager',
    libraries,
    getSentryClient: (options: SentryOptions) => {
      const scope = new Sentry.Scope()
      const client = new Sentry.BrowserClient({
        transport: Sentry.makeFetchTransport,
        stackParser: Sentry.defaultStackParser,
        integrations: Sentry.getDefaultIntegrations({}),
        ...options,
      })
      scope.setClient(client)
      client.init()
      return scope as any
    },
  })
}

const loadDefinitionsFromEditorElementsRegistry = async (
  services: Services,
) => {
  let libraries: ILibraryTopology[]
  try {
    libraries = await getDataBindingDefinitionsLibraries(services)
  } catch (error) {
    services.logger.error(
      new Error('Failed to get Data Binding Definitions libraries', {
        cause: error,
      }),
    )
    return {}
  }

  try {
    const bindingDefinitionRegistry =
      await initBindingDefinitionsRegistry(libraries)
    return await bindingDefinitionRegistry.loadAllComponents()
  } catch (error) {
    services.logger.error(
      new Error('Failed to load definitions from editor elements registry', {
        cause: error,
      }),
      {
        libraries,
      },
    )

    return {}
  }
}

export async function BindingDefinitionProvider(services: Services) {
  const { features } = services

  const localDefinitions: Record<string, DataBindingComponentModel> =
    Object.fromEntries(
      entries(bindingDefinitionsByType)
        .map(([key, bindingDefinitionCreator]) => [
          key,
          bindingDefinitionCreator({
            experimentsManager: services.experimentsManager,
          }),
        ])
        .filter(([_key, definition]) => definition),
    )

  let additionalBindingDefinitions: Record<string, DataBindingComponentModel> =
    {}

  if (features.onlyLocalBindingDefinitions) {
    additionalBindingDefinitions = {
      ...eeDefinitionsByType,
      ...mobuiDefinitionsByType,
    }
  } else {
    const registryDefinitions: Record<string, IDataBindingComponentModel> =
      await loadDefinitionsFromEditorElementsRegistry(services)

    additionalBindingDefinitions = Object.fromEntries(
      Object.entries(registryDefinitions).flatMap(
        ([key, { dataBindingPanel }]) =>
          dataBindingPanel ? [[key, dataBindingPanel]] : [],
      ),
    )

    await Promise.all(
      Object.values(registryDefinitions).map(
        async ({ translations: createTranslations }) => {
          if (createTranslations) {
            try {
              const translationKeyMaps = await createTranslations(
                services.locale,
              )
              translationKeyMaps.forEach(translationKeyMap => {
                services.i18n.addResources(
                  services.locale,
                  'translation',
                  translationKeyMap,
                )
              })
            } catch (e: unknown) {
              services.logger.error(
                new Error(
                  `Failed to create translations from registryDefinitions`,
                  { cause: e },
                ),
              )
            }
          }
        },
      ),
    )
  }

  const bindingDefinitions = {
    ...localDefinitions,
    ...additionalBindingDefinitions,
  }

  return {
    get(componentType: string): DataBindingComponentModel {
      return bindingDefinitions[componentType] || null
    },

    getAll(): Record<string, DataBindingComponentModel> {
      return bindingDefinitions
    },
  }
}

export type BindingDefinitionProviderCreator = typeof BindingDefinitionProvider
